import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { FieldConfig } from "../../formConfig";

interface UploadedFile {
  name: string;
  url: string;
}

export default function FileField({ field }: { field: FieldConfig }) {
  const { setValue, watch, register } = useFormContext();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = watch(field.id) as UploadedFile | UploadedFile[] | undefined;

  const files: UploadedFile[] = Array.isArray(current) ? current : current ? [current] : [];
  const maxFiles = field.maxFiles ?? Infinity;
  const maxSizeMB = field.maxFileSizeMB ?? Infinity;

  // Register the field so its value is included in form submission
  useEffect(() => {
    register(field.id);
  }, [register, field.id]);

  const uploadFile = async (file: File): Promise<UploadedFile> => {
    const sasRes = await fetch(`/api/upload-sas?filename=${encodeURIComponent(file.name)}`);
    if (!sasRes.ok) throw new Error("Failed to get upload URL");
    const { uploadUrl, blobUrl } = await sasRes.json();

    try {
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": file.type || "application/octet-stream",
          "Content-Length": String(file.size)
        },
        body: file,
        mode: "cors"
      });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
      return { name: file.name, url: blobUrl };
    } catch {
      // CORS blocked - fall back to proxy upload
      const formData = new FormData();
      formData.append("file", file);
      const proxyRes = await fetch("/api/upload-proxy", { method: "POST", body: formData });
      if (!proxyRes.ok) throw new Error(`Upload failed (${proxyRes.status})`);
      const { blobUrl: proxyBlobUrl } = await proxyRes.json();
      return { name: file.name, url: proxyBlobUrl };
    }
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files ?? []);
    if (!selectedFiles.length) return;

    // Enforce max files
    const availableSlots = maxFiles - files.length;
    if (selectedFiles.length > availableSlots) {
      setError(`You can only upload ${maxFiles} file${maxFiles === 1 ? "" : "s"} total. ${files.length} already uploaded.`);
      e.target.value = "";
      return;
    }

    // Enforce max size
    const oversized = selectedFiles.filter((f) => f.size > maxSizeMB * 1024 * 1024);
    if (oversized.length > 0) {
      setError(`File${oversized.length > 1 ? "s" : ""} exceed ${maxSizeMB} MB limit: ${oversized.map((f) => f.name).join(", ")}`);
      e.target.value = "";
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const uploaded = await Promise.all(selectedFiles.map(uploadFile));
      const newFiles = [...files, ...uploaded];
      // Store as single object for backwards compatibility when maxFiles is 1
      const value = maxFiles === 1 ? newFiles[0] : newFiles;
      setValue(field.id, value, { shouldValidate: true, shouldDirty: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const removeFile = (idx: number) => {
    const newFiles = files.filter((_, i) => i !== idx);
    if (newFiles.length === 0) {
      setValue(field.id, undefined, { shouldValidate: true, shouldDirty: true });
    } else {
      setValue(field.id, maxFiles === 1 ? newFiles[0] : newFiles, { shouldValidate: true, shouldDirty: true });
    }
  };

  const canAddMore = files.length < maxFiles;

  return (
    <div>
      {canAddMore && (
        <input
          type="file"
          multiple={maxFiles > 1}
          onChange={handleChange}
          disabled={busy}
        />
      )}
      {busy && <div className="hint">Uploading…</div>}
      {files.length > 0 && (
        <ul className="file-list">
          {files.map((f, i) => (
            <li key={i} className="file-list-item">
              <span className="hint">{f.name}</span>
              <button type="button" className="btn-link" onClick={() => removeFile(i)}>✕</button>
            </li>
          ))}
        </ul>
      )}
      {!canAddMore && <div className="hint">Maximum {maxFiles} file{maxFiles === 1 ? "" : "s"} reached.</div>}
      {maxSizeMB < Infinity && <div className="hint">Max {maxSizeMB} MB per file</div>}
      {error && <div className="error">{error}</div>}
    </div>
  );
}
